import { useEffect, useMemo, useRef } from 'react';
import { useCalendarEvents } from '../../hooks/useCalendar';
import { HOUR_PX, layoutDay, splitAllDay } from '../../lib/calendar/timeGridLayout';
import { dayKeyParts, isDayKey, MONTH_NAMES, rangeFor, WEEKDAY_NAMES } from '../../lib/calendar/zonedCalendar';

const SLOT_MIN = 30;
const hourLabel = (h) => (h === 0 ? '' : `${h % 12 === 0 ? 12 : h % 12} ${h < 12 ? 'AM' : 'PM'}`);
const toMin = (hhmm) => { const [h, m] = String(hhmm).split(':').map(Number); return h * 60 + m; };
const fmt = (min) => {
    const h = Math.floor(min / 60) % 24;
    const m = min % 60;
    return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};

/**
 * The editor's right panel (#680, prototype `newDayEvents`): the chosen day's
 * other events in grey, the event being authored in brand violet, and a click
 * anywhere to move the start (duration kept). Laid out by the same
 * timeGridLayout the main grid uses, in the EDITOR's zone — the zone the
 * times being typed are in.
 */
export function DayTimelinePanel({ date, startTime, endTime, allDay, timezone, excludeEventId, onPickStart }) {
    const scrollerRef = useRef(null);
    const valid = isDayKey(date);
    const { fromIso, toIso } = rangeFor('day', valid ? date : '1970-01-01', timezone);
    const { data } = useCalendarEvents(fromIso, toIso, { enabled: valid });
    const others = useMemo(
        () => (data?.events || []).filter(e => e.event_id !== excludeEventId),
        [data, excludeEventId],
    );
    const blocks = useMemo(() => {
        if (!valid) return [];
        const { timed } = splitAllDay(others, [date], timezone);
        return layoutDay(timed[date] || []);
    }, [others, date, timezone, valid]);

    const startMin = toMin(startTime);
    const endMin = toMin(endTime);
    const selectionValid = !allDay && Number.isFinite(startMin) && endMin > startMin;

    useEffect(() => {
        if (scrollerRef.current && selectionValid) {
            scrollerRef.current.scrollTop = Math.max(0, (startMin / 60 - 2) * HOUR_PX);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    const p = valid ? dayKeyParts(date) : null;
    return (
        <aside className="hidden md:flex w-[300px] flex-none box-border bg-[#FBFAF9] border-l border-[#E9E7E2] flex-col min-h-0" aria-label="Day timeline" data-testid="editor-day-timeline">
            <div className="flex flex-none items-center gap-2 px-4 py-[13px] border-b border-[#EDEBE6]">
                <span className="flex-1 text-[13px] font-semibold text-[#0C0A09]">
                    {p ? `${WEEKDAY_NAMES[p.weekday]}, ${p.day} ${MONTH_NAMES[p.month]} ${p.year}` : 'Pick a date'}
                </span>
                <span className="text-[11.5px] text-[#A29B95]">{allDay ? 'All day' : selectionValid ? `${fmt(startMin)} – ${fmt(endMin)}` : ''}</span>
            </div>
            <div ref={scrollerRef} className="flex-1 min-h-0 overflow-y-auto">
                <div className="flex relative pt-2.5">
                    <div className="w-[52px] flex-none border-r border-[#F2F0EC]">
                        {Array.from({ length: 24 }, (_, h) => (
                            <div key={h} className="relative" style={{ height: HOUR_PX }}>
                                <span className="absolute -top-[7px] right-2 text-[10px] text-[#A29B95] whitespace-nowrap">{hourLabel(h)}</span>
                            </div>
                        ))}
                    </div>
                    <div className="flex-1 min-w-0 relative">
                        {Array.from({ length: (24 * 60) / SLOT_MIN }, (_, i) => {
                            const min = i * SLOT_MIN;
                            const hh = String(Math.floor(min / 60)).padStart(2, '0');
                            const mm = String(min % 60).padStart(2, '0');
                            return (
                                <button
                                    key={min}
                                    type="button"
                                    tabIndex={-1}
                                    aria-hidden="true"
                                    onClick={() => onPickStart(`${hh}:${mm}`)}
                                    className={`block w-full box-border border-b ${min % 60 ? 'border-[#F8F6F3]' : 'border-[#F4F2EE]'} hover:bg-[#F4F2EE]`}
                                    style={{ height: (HOUR_PX * SLOT_MIN) / 60 }}
                                />
                            );
                        })}
                        {blocks.map(({ segment, top, height, lane, lanes }) => (
                            <div
                                key={segment.event.event_id}
                                className="absolute box-border overflow-hidden rounded-[7px] px-[7px] py-[3px] bg-[#F1EFEA] text-[#57504B] border-l-2 border-[#C9C4BD] z-[2] pointer-events-none"
                                style={{ top, height: Math.max(22, height), left: `calc(${(lane / lanes) * 100}% + 4px)`, width: `calc(${100 / lanes}% - 12px)` }}
                            >
                                <div className="text-[11px] font-semibold leading-[1.3] truncate">{segment.event.title}</div>
                            </div>
                        ))}
                        {selectionValid && (
                            <div
                                className="absolute left-1 right-2 box-border px-2 py-1 rounded-lg bg-[#4D11F6] text-white text-[11px] font-semibold z-[4] shadow-[0_2px_8px_rgba(77,17,246,.28)] pointer-events-none"
                                style={{ top: (startMin / 60) * HOUR_PX + 1, height: Math.max(22, ((endMin - startMin) / 60) * HOUR_PX - 2) }}
                                data-testid="editor-day-selection"
                            >
                                {fmt(startMin)} – {fmt(endMin)}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </aside>
    );
}
